/**
 * WASSER Admin - Theme Factory
 * Центральная точка создания темы
 */
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import * as tokens from '@shared/theme/tokens.js';
import { getComponents } from './components';
import { getPalette } from './palette';
import typography from './typography';

// MUI spacing unit (8px grid). Component-level sizes should rely on design tokens.
const spacing = 8;

// Breakpoints (source of truth: shared tokens)
const breakpoints = {
  values: {
    xs: tokens.breakpoints.xs,
    sm: tokens.breakpoints.sm,
    md: tokens.breakpoints.md,
    lg: tokens.breakpoints.lg,
    xl: tokens.breakpoints.xl,
  },
};

// Border radius (source of truth: shared tokens)
const shape = {
  borderRadius: tokens.borderRadius.md,
};

// Shadows для разных режимов
const getShadows = (mode) => {
  const isDark = mode === 'dark';

  // MUI expects an array of 25 shadows
  const baseShadows = [
    'none',
    isDark ? '0 1px 3px rgba(0, 0, 0, 0.4)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
    isDark ? '0 2px 6px rgba(0, 0, 0, 0.5)' : '0 2px 6px rgba(0, 0, 0, 0.1)',
    isDark ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.12)',
    isDark
      ? '0 6px 16px rgba(0, 0, 0, 0.55)'
      : '0 6px 16px rgba(0, 0, 0, 0.14)',
    isDark ? '0 8px 20px rgba(0, 0, 0, 0.6)' : '0 8px 20px rgba(0, 0, 0, 0.15)',
    isDark
      ? '0 10px 24px rgba(0, 0, 0, 0.6)'
      : '0 10px 24px rgba(0, 0, 0, 0.16)',
    isDark
      ? '0 12px 28px rgba(0, 0, 0, 0.65)'
      : '0 12px 28px rgba(0, 0, 0, 0.18)',
    isDark
      ? '0 14px 32px rgba(0, 0, 0, 0.7)'
      : '0 14px 32px rgba(0, 0, 0, 0.2)',
  ];

  // Fill remaining shadows (9-24) with increasing intensity
  while (baseShadows.length < 25) {
    const i = baseShadows.length;
    const blur = 16 + i * 2;
    const opacity = isDark ? 0.5 + i * 0.02 : 0.1 + i * 0.01;
    baseShadows.push(
      isDark
        ? `0 ${i}px ${blur}px rgba(0, 0, 0, ${Math.min(opacity, 0.8)})`
        : `0 ${i}px ${blur}px rgba(0, 0, 0, ${Math.min(opacity, 0.3)})`
    );
  }

  return baseShadows;
};

/**
 * Создание темы для указанного режима
 * @param {string} mode - 'light' | 'dark'
 * @returns {object} MUI Theme
 */
export const getTheme = (mode = 'light') => {
  const palette = getPalette(mode);

  const baseTheme = createTheme({
    palette,
    typography,
    spacing,
    breakpoints,
    shape,
    shadows: getShadows(mode),
    components: getComponents(mode, palette),
  });

  // Responsive font sizes для h1-h6
  return responsiveFontSizes(baseTheme);
};

// Экспорт темы по умолчанию (light)
const theme = getTheme('light');

export default theme;
